/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { accessAPI, Boton, Titulo, useOrganismo, deOrganismo, tituloBase, TablaFiltrable } from "../../shared";

import { avanceProceso, diasHasta, fechaCorta, textoPaso, textoSinEtapa } from "../../portal/utilidades";
import RequiereAtencion from "./RequiereAtencion";
import { PanelOperaciones } from "./Operaciones";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { ProgressBar } from "primereact/progressbar";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";

export default function Inicio() {
  // Título de la pestaña, como en el admin
  useEffect(() => {
    document.title = "Inicio";
    return () => (document.title = tituloBase());
  }, []);

  const organismo = useOrganismo();
  // El nombre del usuario y la empresa con la que opera (el Header los pide
  // por su cuenta, igual que en el admin)
  const [usuario, setUsuario] = useState(null);
  const [accesos, setAccesos] = useState(null);
  useEffect(() => {
    accessAPI(
      "GET",
      "usuario/me",
      null,
      (r) => setUsuario(r.usuario),
      () => {}
    );
    accessAPI("GET", "usuario/info-accesos", null, setAccesos, () => {});
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useRef(null);
  const [operacionesVisible, setOperacionesVisible] = useState(false);

  const [certificados, setCertificados] = useState(null);
  const [actividades, setActividades] = useState(null);
  const [solicitudes, setSolicitudes] = useState(null);
  const [hallazgos, setHallazgos] = useState(null);

  useEffect(() => {
    accessAPI("GET", "cliente/instanciacertificado", null, setCertificados, () => setCertificados([]));
    // Actividades abiertas: la API devuelve una fila por paso actual (con pasos
    // en paralelo, más de una por actividad); acá va una por actividad
    accessAPI(
      "GET",
      "cliente/proceso/actividades",
      null,
      (pasos) => {
        const porId = new Map();
        pasos.forEach((p) => porId.has(p.actividad.id) || porId.set(p.actividad.id, p.actividad));
        setActividades([...porId.values()]);
      },
      () => setActividades([])
    );
    accessAPI("GET", "cliente/proceso/solicitudes", null, setSolicitudes, () => setSolicitudes([]));
    accessAPI("GET", "cliente/hallazgo", null, setHallazgos, () => setHallazgos([]));
  }, []);

  // Mensajes que dejan otras pantallas al volver al inicio (ej. "Solicitud
  // ingresada correctamente" desde el asistente de nueva solicitud).
  useEffect(() => {
    const estado = location.state;
    if (estado?.showToast) {
      toast.current?.show({
        severity: "success",
        summary: "Éxito",
        detail: estado.toastMessage,
        life: 3000
      });
    }
    if (estado?.mensaje) {
      toast.current?.show({ severity: "info", detail: estado.mensaje, life: 5000 });
    }
    if (estado?.showToast || estado?.mensaje) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const porVencer = useMemo(
    () =>
      (certificados || [])
        .map((c) => ({ ...c, dias: diasHasta(c.fechavencimiento) }))
        .filter((c) => c.dias !== null && c.dias <= 60 && !c.proceso?.certificadotipo?.sinrenovacion)
        .sort((a, b) => a.dias - b.dias),
    [certificados]
  );

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Titulo
        etiqueta={accesos?.clienteActual?.razonsocial}
        botones={
          <>
            {/* Siempre visible, como en el portal anterior */}
            <Boton transparente callback={() => setOperacionesVisible(true)}>
              Operaciones
            </Boton>
            <Boton uri="/nuevoproceso">Nueva solicitud</Boton>
          </>
        }
      >
        Hola{usuario?.nombre ? `, ${usuario.nombre}` : ""}
      </Titulo>

      {/* Grid de PrimeFlex, como en el admin: 1em entre cards; solicitudes y
          vencimientos lado a lado en web */}
      <div className="grid">
        <div className="col-12">
          <RequiereAtencion />
        </div>

        <div className="col-12">
          <Card>
            <Titulo
              etiqueta="Nuevo" neutro
              subtitulo="Avance de las actividades abiertas de tus procesos"
              botones={
                <Boton texto uri="/procesos">
                  Ver todas
                </Boton>
              }
            >
              <i className="pi pi-sitemap" css={{ marginRight: "0.5em" }} />
              Actividades en curso
            </Titulo>
            {/* Una fila por actividad: nombre, paso y barra de avance */}
            {!actividades &&
              [...Array(3)].map((_, i) => (
                <div key={i} css={{ padding: "0.9rem 0", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                  <Skeleton width="45%" height="1.1rem" />
                  <Skeleton width="30%" height="0.9rem" />
                  <Skeleton height="0.5rem" />
                </div>
              ))}
            {actividades && actividades.length === 0 && (
              <div css={{ textAlign: "center", padding: "2em 1em", color: "var(--grisTextos)" }}>
                <i className="pi pi-sitemap" css={{ fontSize: "1.5rem" }} />
                <div css={{ fontWeight: 600, color: "rgba(44, 44, 44, 1)", marginTop: "0.5em" }}>
                  No tienes actividades abiertas
                </div>
              </div>
            )}
            {actividades && actividades.length > 0 && (
              <div css={{ display: "flex", flexDirection: "column" }}>
                {actividades.slice(0, 4).map((a, i) => (
                  <FilaActividad
                    key={a.id}
                    actividad={a}
                    primero={i === 0}
                    hallazgosPendientes={
                      hallazgos &&
                      hallazgos.filter((h) => h.estado === "PENDIENTE_RESPUESTA" && h.actividadId === a.id).length
                    }
                  />
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="col-12 lg:col-6">
          <Card css={{ height: "100%" }}>
            <Titulo etiqueta="Nuevo" neutro subtitulo={`Todavía no convertidas en proceso por ${organismo}`}>
              <i className="pi pi-send" css={{ marginRight: "0.5em" }} />
              Solicitudes enviadas
            </Titulo>
            {/* Una fila por solicitud: nombre y tipo, y la etiqueta de estado */}
            {!solicitudes &&
              [...Array(2)].map((_, i) => (
                <div
                  key={i}
                  css={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", padding: "0.75rem 0" }}
                >
                  <div css={{ flex: 1 }}>
                    <Skeleton width="60%" height="1rem" css={{ marginBottom: "0.4em" }} />
                    <Skeleton width="40%" height="0.8rem" />
                  </div>
                  <Skeleton width="8rem" height="1.5rem" borderRadius="999px" />
                </div>
              ))}
            {solicitudes && solicitudes.length === 0 && (
              <div css={{ textAlign: "center", padding: "2em 1em", color: "var(--grisTextos)" }}>
                <i className="pi pi-send" css={{ fontSize: "1.5rem" }} />
                <div css={{ fontWeight: 600, color: "rgba(44, 44, 44, 1)", marginTop: "0.5em" }}>
                  No hay solicitudes en revisión
                </div>
                <div css={{ marginTop: "0.75em" }}>
                  <Boton transparente uri="/nuevoproceso">
                    Iniciar una solicitud
                  </Boton>
                </div>
              </div>
            )}
            {/* Todas: no hay otra pantalla con las solicitudes enviadas */}
            {solicitudes?.length > 0 && (
              <TablaFiltrable conFiltros={false} value={solicitudes} dataKey="id" size="small" nombreArchivo="solicitudes_enviadas">
                <Column header="Solicitud" field="nombrefantasia" />
                <Column
                  header="Estado"
                  body={() => (
                    <Tag severity="secondary" icon="pi pi-hourglass">
                      En revisión por {organismo}
                    </Tag>
                  )}
                  exportValue={() => `En revisión por ${organismo}`}
                />
              </TablaFiltrable>
            )}
          </Card>
        </div>

        <div className="col-12 lg:col-6">
          <Card css={{ height: "100%" }}>
            <Titulo etiqueta="Nuevo" neutro subtitulo="Próximos 60 días">
              <i className="pi pi-calendar" css={{ marginRight: "0.5em" }} />
              Certificados próximos a vencer
            </Titulo>
            {/* Filas de la tabla mientras carga */}
            {!certificados &&
              [...Array(3)].map((_, i) => <Skeleton key={i} height="2.5rem" css={{ marginBottom: "0.5em" }} />)}
            {certificados && porVencer.length === 0 && (
              <div css={{ textAlign: "center", padding: "2em 1em", color: "var(--grisTextos)" }}>
                <i className="pi pi-calendar" css={{ fontSize: "1.5rem" }} />
                <div css={{ fontWeight: 600, color: "rgba(44, 44, 44, 1)", marginTop: "0.5em" }}>
                  Ningún certificado vence pronto
                </div>
              </div>
            )}
            {porVencer.length > 0 && (
              <TablaFiltrable conFiltros={false}
                value={porVencer}
                dataKey="id"
                size="small"
                nombreArchivo="certificados_por_vencer"
                onRowClick={(e) => navigate(`/certificado/${e.data.id}?volver=/`)}
                css={{ "& .p-datatable-tbody > tr": { cursor: "pointer" } }}
              >
                <Column header="Certificado" field="numero" />
                <Column header="Nombre" field="proceso.nombrefantasia" />
                <Column
                  header="Vencimiento"
                  body={(c) => (
                    <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem", alignItems: "flex-start" }}>
                      <span>{fechaCorta(c.fechavencimiento)}</span>
                      {c.dias != null && (
                        <Tag
                          // Días hasta el vencimiento: rojo si venció, ámbar hasta 60, verde después
                          severity={c.dias < 0 ? "danger" : c.dias <= 60 ? "warning" : "success"}
                          icon={c.dias < 0 ? "pi pi-calendar-times" : "pi pi-calendar"}
                        >
                          {c.dias < 0 ? "Vencido" : `${c.dias} ${c.dias === 1 ? "día" : "días"}`}
                        </Tag>
                      )}
                    </div>
                  )}
                  exportValue={(c) => fechaCorta(c.fechavencimiento)}
                />
              </TablaFiltrable>
            )}
          </Card>
        </div>
      </div>

      <PanelOperaciones visible={operacionesVisible} onHide={() => setOperacionesVisible(false)} />
    </>
  );
}

// Una fila de "Actividades en curso", con estilos propios (emotion). Arriba a
// la izquierda: proceso, actividad y el paso en el que está; a la derecha, de
// qué lado está y los días en el paso. Abajo, la barra con los pasos hechos.
// En mobile el bloque de la derecha baja debajo del nombre.
function FilaActividad({ actividad, primero, hallazgosPendientes }) {
  const organismo = useOrganismo();
  const navigate = useNavigate();
  const proceso = actividad.proceso;
  // El avance de una sola actividad: mismo cálculo que el del proceso
  const avance = avanceProceso({ actividad: [actividad] });
  const dondeEsta = avance.etapaActual ? textoPaso(avance) : textoSinEtapa(avance);
  return (
    <div
      onClick={() => navigate(`/actividades/${actividad.id}?volver=/`)}
      css={{
        cursor: "pointer",
        padding: "0.9rem 0.5rem",
        borderTop: primero ? "none" : "1px solid #e5e7eb",
        display: "flex",
        flexDirection: "column",
        gap: "0.6rem",
        minWidth: 0,
        "&:hover .titulo-actividad": { color: "var(--principal)" }
      }}
    >
      <div
        css={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "0.6rem 1rem",
          flexWrap: "wrap"
        }}
      >
        <div css={{ display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: 0, flex: "1 1 280px" }}>
          <div css={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
            <Tag value={proceso.nombre} />
            <div css={{ minWidth: 0 }}>
              <div className="titulo-actividad" css={{ fontWeight: 600, transition: "color 0.15s" }}>
                {avance.etapaActual || avance.actividades[0]?.nombre}
              </div>
              <div css={{ fontSize: "0.85em", color: "var(--grisTextos)" }}>
                {[proceso.nombrefantasia, proceso.certificadotipo?.nombre].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
          <span css={{ fontSize: "0.9em" }}>{dondeEsta}</span>
        </div>
        <div css={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.35rem" }}>
          {/* De qué lado está la actividad: con hallazgos que esperan respuesta
              del cliente, le toca al cliente; si no, en teoría al organismo.
              (undefined mientras cargan los hallazgos: no se muestra nada) */}
          {hallazgosPendientes > 0 ? (
            <Tag severity="danger" icon="pi pi-exclamation-triangle" value="Pendiente de tu parte" />
          ) : hallazgosPendientes === 0 && avance.etapaActual ? (
            <Tag severity="secondary" icon="pi pi-hourglass" value={`En manos ${deOrganismo(organismo)}`} />
          ) : null}
          {avance.etapaActual && avance.diasEnEtapa != null && (
            <Tag
              // Días en el paso: verde hasta 3, ámbar hasta 5, rojo después
              severity={avance.diasEnEtapa <= 3 ? "success" : avance.diasEnEtapa <= 5 ? "warning" : "danger"}
              icon="pi pi-clock"
              value={`${avance.diasEnEtapa} ${avance.diasEnEtapa === 1 ? "día" : "días"} en este paso`}
            />
          )}
        </div>
      </div>

      {avance.total > 0 && (
        <div css={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <ProgressBar
            value={avance.porcentaje}
            showValue={false}
            css={{
              flex: 1,
              height: "0.5rem",
              "& .p-progressbar-value": { background: "var(--principal)" }
            }}
          />
          <span css={{ fontSize: "0.85em", color: "var(--grisTextos)", whiteSpace: "nowrap" }}>
            {avance.finalizados} de {avance.total} pasos
          </span>
        </div>
      )}
    </div>
  );
}
